import type { ConfigurationSections } from "../../../../../../types/clientConfig";
import {
  type ClientConfigWidgetAvailability,
  getSectionWidgetAvailability,
  isSectionDisabledByMissingWidget,
} from "../../../../../athlete-portal/profile/components/ClientConfiguration/utils";

/** Sections that can show a "Needs attention" warning in the config list. */
export const ATTENTION_SECTIONS = [
  "training",
  "nutrition",
  "mealPlans",
  "dailyAccountabilities",
  "goals",
] as const;

export type AttentionSection = (typeof ATTENTION_SECTIONS)[number];

/**
 * - `clientNoWidget`: client mode on Meal Plans / Accountabilities, but no
 *   published pull widget for the client to pick from.
 * - `coachNoData`: coach mode, but the coach hasn't assigned anything yet.
 */
export type AttentionReason = "clientNoWidget" | "coachNoData";

const WIDGET_SECTIONS: AttentionSection[] = [
  "mealPlans",
  "dailyAccountabilities",
];

/**
 * Why each section of a row needs attention, if it does. The config list
 * filter in msb-api (lib/api_helpers/client-configuration/config-filters.js,
 * `attentionSections`) applies the same rule — keep the two in sync, or the
 * "Needs attention" filter will disagree with the warnings in the table.
 *
 * @param sectionHasData - from the section-turn-off-validator batch; null
 *   while it hasn't loaded, which suppresses `coachNoData`.
 */
export function getAttentionReasons(
  sections: ConfigurationSections,
  sectionHasData: Record<string, boolean> | null,
  widgetAvailability?: ClientConfigWidgetAvailability
): Partial<Record<AttentionSection, AttentionReason>> {
  const availability = getSectionWidgetAvailability(widgetAvailability);
  const reasons: Partial<Record<AttentionSection, AttentionReason>> = {};

  for (const key of ATTENTION_SECTIONS) {
    const mode = sections[key]?.mode;
    if (
      WIDGET_SECTIONS.includes(key) &&
      mode === "client" &&
      isSectionDisabledByMissingWidget(key, availability)
    ) {
      reasons[key] = "clientNoWidget";
    } else if (
      sectionHasData !== null &&
      mode === "coach" &&
      !sectionHasData[key]
    ) {
      reasons[key] = "coachNoData";
    }
  }

  return reasons;
}
